<template>
  <div class="content is-small lane-review-content">
    <div v-if="isPopup" class="has-text-centered mb-2">
      <h3 class="title is-6 mb-1 has-text-left">{{ lane.name }}</h3>
    </div>
    <div v-else>
      <h3 class="title is-5 has-text-left">Review Lane Details</h3>
    </div>

    <!-- Tabs navigation -->
    <div class="tabs is-small is-success">
      <ul>
        <li :class="{ 'is-active': activeTab === 'basic' }">
          <a @click="activeTab = 'basic'">Basic Info</a>
        </li>
        <li :class="{ 'is-active': activeTab === 'segments' }">
          <a @click="activeTab = 'segments'">Segments</a>
        </li>
        <li :class="{ 'is-active': activeTab === 'additional' }">
          <a @click="activeTab = 'additional'">Additional Details</a>
        </li>
      </ul>
    </div>

    <!-- Tab 1: Basic Info -->
    <div v-show="activeTab === 'basic'" style="min-height: 260px;">
      <p class="detail-row"><strong>Name:</strong> {{ lane.name }}</p>
      <p class="detail-row"><strong>Distribution Zones:</strong> {{selectedHubs.map(hub =>
        hub.DistributionZoneName).join(' → ')}}</p>
      <p class="detail-row"><strong>Total Lead Time:</strong> {{ lane.totalLeadTime }} days</p>
      <p class="detail-row"><strong>Capacity:</strong> {{ lane.capacity }}</p>
      <p class="detail-row"><strong>Cost:</strong> ${{ lane.cost }}</p>
    </div>

    <!-- Tab 2: Segments -->
    <div v-show="activeTab === 'segments'" style="min-height: 260px;">
      <div class="box has-background-light p-2 mb-2">
        <h4 class="title is-6 has-text-left mb-1">Segments</h4>

        <!-- Carousel Content -->
        <div class="carousel-container" style="overflow: hidden; position: relative;">
          <div class="carousel-slide" style="transition: transform 0.3s ease;">
            <div v-if="lane.segments.length > 0" class="mb-2">
              <p class="detail-row">
                <strong>{{ lane.segments[currentSegmentIndex].from.DistributionZoneName }} →
                  {{ lane.segments[currentSegmentIndex].to.DistributionZoneName }}</strong>
              </p>
              <p class="detail-row">Lead Time: {{ lane.segments[currentSegmentIndex].leadTime }} days
              </p>

              <!-- Vehicle Restrictions as list -->
              <div class="detail-section">
                <strong>Vehicle Restrictions:</strong>
                <ul
                  v-if="lane.segments[currentSegmentIndex].vehicleTypeRestrictions && lane.segments[currentSegmentIndex].vehicleTypeRestrictions.length"
                  class="detail-list">
                  <li v-for="restriction in lane.segments[currentSegmentIndex].vehicleTypeRestrictions" :key="restriction">
                    {{ getVehicleTypeRestrictionLabel(restriction) }}
                  </li>
                </ul>
                <span v-else>None</span>
              </div>

              <p class="detail-row">Max Weight: {{
                lane.segments[currentSegmentIndex].loadRestrictions.maxWeight || 'Not specified' }}
              </p>
              <p class="detail-row">Hazardous Materials: {{
                lane.segments[currentSegmentIndex].loadRestrictions.hazardousMaterials ?
                  'Restricted' : 'Allowed' }}</p>
            </div>
            <div v-else class="has-text-centered">
              No segments defined
            </div>
          </div>
        </div>

        <!-- Carousel Navigation -->
        <div class="is-flex is-justify-content-space-between mb-1" v-if="lane.segments.length > 1">
          <button class="button is-small" @click="prevSegment" :disabled="currentSegmentIndex === 0">
            <span class="icon is-small">
              <i class="fas fa-chevron-left"></i>
            </span>
          </button>
          <span>{{ currentSegmentIndex + 1 }} / {{ lane.segments.length }}</span>
          <button class="button is-small" @click="nextSegment"
            :disabled="currentSegmentIndex >= lane.segments.length - 1">
            <span class="icon is-small">
              <i class="fas fa-chevron-right"></i>
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Tab 3: Additional Details -->
    <div v-show="activeTab === 'additional'" style="min-height: 260px;">
      <!-- Display additional parameters -->
      <p class="detail-row"><strong>Freight Type:</strong> {{ lane.freightType.length ?
        lane.freightType.join(', ') : 'None specified' }}</p>
      <p class="detail-row"><strong>Time-of-Day Restrictions:</strong> {{ lane.timeOfDayRestrictions ||
        'None' }}</p>

      <!-- Environmental considerations as list -->
      <div class="detail-section">
        <strong>Environmental Considerations:</strong>
        <ul v-if="lane.environmentalConsiderations && lane.environmentalConsiderations.length"
          class="detail-list">
          <li v-for="consideration in lane.environmentalConsiderations" :key="consideration">
            {{ getEnvironmentalConsiderationLabel(consideration) }}
          </li>
        </ul>
        <span v-else>None</span>
      </div>

      <!-- Technology integration as list -->
      <div class="detail-section">
        <strong>Technology Integration:</strong>
        <ul v-if="lane.technologyIntegration && lane.technologyIntegration.length" class="ml-4 mb-0 mt-0 pl-3">
          <li v-for="tech in lane.technologyIntegration" :key="tech">
            {{ getTechnologyIntegrationLabel(tech) }}
          </li>
        </ul>
        <span v-else>None</span>
      </div>

      <!-- Platooning regulations -->
      <p class="detail-row"><strong>Platooning:</strong> {{ lane.platooningRegulations.allowed ? 'Allowed' :
        'Not allowed' }}</p>
      <p class="detail-row" v-if="lane.platooningRegulations.allowed">
        <strong>Minimum Following Distance:</strong> {{ lane.platooningRegulations.minFollowingDistance }}
        meters
      </p>
    </div>

    <!-- Close button for popup context -->
    <div class="is-flex is-justify-content-flex-end mt-2" v-if="isPopup">
      <div class="buttons">
        <button class="button is-small is-danger" :class="{ 'is-loading': isDeleting }" style="border-radius: 5px;"
          @click="deleteLane(lane.id)">
          Delete
        </button>
        <button class="button is-small is-primary mr-2" :class="{ 'is-loading': isDeleting }" style="border-radius: 5px;"
          @click="editLane">
          Edit
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { lanesCollection } from "@/store/fireStore";

export default {
  name: "LaneReview",
  props: {
    lane: {
      type: Object,
      required: true
    },
    selectedHubs: {
      type: Array,
      required: true
    },
    currentSegmentIndex: {
      type: Number,
      default: 0
    },
    isPopup: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      activeTab: 'basic',
      isDeleting: false
    };
  },
  computed: {
    // Maps for displaying labels instead of values
    vehicleTypeRestrictionsMap() {
      return {
        commercialAboveWeightClass: 'Limit to commercial vehicles above a certain weight class',
        restrictPassengerVehicles: 'Restrict passenger vehicles'
      };
    },
    environmentalConsiderationsMap() {
      return {
        lowEmissionVehicles: 'Restrict to low-emission or electric vehicles',
        noiseReduction: 'Implement noise reduction measures'
      };
    },
    technologyIntegrationMap() {
      return {
        gpsTracking: 'Require GPS tracking',
        electronicLoggingDevices: 'Require electronic logging devices',
        smartLaneSystems: 'Implement smart lane systems'
      };
    },
  },
  methods: {
    // Helper methods to map values to labels
    getVehicleTypeRestrictionLabel(value) {
      return this.vehicleTypeRestrictionsMap[value] || value;
    },
    getEnvironmentalConsiderationLabel(value) {
      return this.environmentalConsiderationsMap[value] || value;
    },
    getTechnologyIntegrationLabel(value) {
      return this.technologyIntegrationMap[value] || value;
    },
    prevSegment() {
      this.$emit('prev-segment');
    },
    nextSegment() {
      this.$emit('next-segment');
    },
    editLane() {
      this.$emit('edit', this.lane);
    },
    close() {
      this.$emit('close');
    },
    async deleteLane(laneId) {
      try {
        this.isDeleting = true;
        await lanesCollection.doc(laneId).delete();
        this.isDeleting = false;
        this.$emit('close');
        // No need to call retrieveData as the onSnapshot listener will update the store
      } catch (error) {
        console.error("Error deleting lane:", error);
        // You might want to add error handling/notification here
      }
    },
  }
};
</script>

<style scoped>
/* Consistent styling for detail rows */
.detail-row {
  margin-bottom: 0.5rem;
  padding-left: 0.25rem;
  text-align: left;
  line-height: 1.4;
}

.detail-section {
  margin-bottom: 0.5rem;
  text-align: left;
  padding-left: 0.25rem;
}

.detail-list {
  margin-left: 1.5rem !important;
  margin-top: 0.25rem !important;
  margin-bottom: 0.25rem !important;
  padding-left: 0.5rem !important;
}

.detail-list li {
  margin-bottom: 0.25rem;
  line-height: 1.3;
}

.lane-review-content {
  padding: 20px !important;
}

/* Button styling */
.button.is-info {
  background-color: #209cee;
  color: white;
}

.button.is-info:hover {
  background-color: #1d91db;
}

.button.is-dark {
  border-radius: 5px;
  background-color: #363636;
  color: white;
}

.button.is-dark:hover {
  border-radius: 5px;
  background-color: #292929;
}
</style>

<style>
/* These styles need to be global to affect the Leaflet popup container */
.lane-review-popup .leaflet-popup-content-wrapper {
  padding: 0;
  border-radius: 4px;
  overflow: hidden;
  width: auto !important;
  max-width: 500px;
}

.lane-review-popup .leaflet-popup-content {
  margin: 0;
  padding: 0;
  width: auto !important;
  min-width: 450px;
}

.lane-review-popup .content {
  padding: 0;
  width: 100%;
}

.lane-review-popup .box {
  margin-bottom: 10px;
  padding: 0.75rem;
}

.tabs.is-success {
  li.is-active a {
    border-bottom-color: #007d6a;
    color: #007d6a;
    font-weight: 600;
  }

  a {
    &:hover {
      border-bottom-color: var(--primary);
      color: var(--primary);
    }
  }

  li a {
    color: var(--primary);
  }
}
</style>
